import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { LOGO_URL } from "../data/constants";

const NAV_LINKS = [
  { name: "Home", path: "/" },
  { name: "Why FunAsia", path: "/overview" },
  {
    name: "SmartReach",
    path: "/smartreach",
    dropdown: [
      { name: "SmartReach Platform", path: "/smartreach" },
      { name: "Ads Manager (Self-Serve)", path: "/" },
      { name: "How It Works", path: "/how-it-works" },
      { name: "Foot Traffic Marketing", path: "/foot-traffic-marketing" },
      { name: "Loyalty Platform", path: "/loyalty-platform" },
      { name: "Measurement", path: "/measurement" }
    ]
  },
  {
    name: "Solutions",
    path: "/industries",
    dropdown: [
      { name: "All Industries", path: "/industries" },
      { name: "Restaurants", path: "/industries/restaurants" },
      { name: "Retail", path: "/industries/retail" },
      { name: "Entertainment", path: "/industries/entertainment" },
      { name: "Salons & Wellness", path: "/industries/salons-spas-wellness" },
      { name: "Healthcare", path: "/industries/healthcare" },
      { name: "Automotive", path: "/industries/automotive-dealers" }
    ]
  },
  { name: "Services", path: "/services" },
  {
    name: "Programmatic DSP",
    path: "/dsp",
    dropdown: [
      { name: "Overview", path: "/dsp" },
      { name: "How It Works", path: "/dsp/how-it-works" },
      { name: "Pricing", path: "/dsp/pricing" },
      { name: "FAQ", path: "/dsp/faq" }
    ]
  },
  { name: "Pricing", path: "/pricing" },
  { name: "Resources", path: "/blog" },
  { name: "About", path: "/about" },
  { name: "Contact", path: "/contact" },
  { name: "Store", path: "/store" }
];

const slug = (s) => s.toLowerCase().replace(/\s+/g, "-");

// ---------- Desktop ----------
const DesktopDropdown = ({ link, isActive }) => (
  <div className="relative group">
    <Link
      to={link.path}
      className={`flex items-center gap-1 text-sm font-medium transition-colors duration-200 py-2 ${
        isActive ? "text-orange-primary" : "text-gray-dark hover:text-orange-primary"
      }`}
      data-testid={`nav-${slug(link.name)}`}
    >
      {link.name}
      <ChevronDown size={14} className="transition-transform group-hover:rotate-180" />
    </Link>
    <div className="absolute top-full left-0 pt-1 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
      <div className="w-56 bg-white rounded-xl shadow-lg border border-gray-100 py-2">
        {link.dropdown.map((item) => (
          <Link
            key={item.path}
            to={item.path}
            className="block px-4 py-2 text-sm text-gray-dark hover:bg-orange-light hover:text-orange-primary transition-colors"
            data-testid={`nav-dropdown-${slug(item.name)}`}
          >
            {item.name}
          </Link>
        ))}
      </div>
    </div>
  </div>
);

const DesktopLink = ({ link, isActive }) => (
  <Link
    to={link.path}
    data-testid={`nav-${slug(link.name)}`}
    className={`text-sm font-medium transition-colors duration-200 ${
      isActive ? "text-orange-primary" : "text-gray-dark hover:text-orange-primary"
    }`}
  >
    {link.name}
  </Link>
);

const DesktopNav = ({ links, isActiveFn, onLaunchDsp }) => (
  <div className="hidden lg:flex items-center gap-6">
    {links.map((link) =>
      link.dropdown ? (
        <DesktopDropdown key={link.path} link={link} isActive={isActiveFn(link.path)} />
      ) : (
        <DesktopLink key={link.path} link={link} isActive={isActiveFn(link.path)} />
      )
    )}
    <button onClick={onLaunchDsp} className="btn-primary text-sm" data-testid="nav-launch-dsp-btn">
      Launch DSP
    </button>
  </div>
);

// ---------- Mobile ----------
const MobileDropdown = ({ link, isActive, isOpen, onToggle, onItemClick }) => (
  <div>
    <button
      onClick={onToggle}
      className={`flex items-center justify-between w-full py-3 px-4 text-sm font-medium ${
        isActive ? "text-orange-primary bg-orange-light rounded-lg" : "text-gray-dark"
      }`}
    >
      {link.name}
      <ChevronDown size={14} className={`transition-transform ${isOpen ? "rotate-180" : ""}`} />
    </button>
    {isOpen && (
      <div className="pl-4">
        {link.dropdown.map((item) => (
          <Link
            key={item.path}
            to={item.path}
            onClick={onItemClick}
            className="block py-2 px-4 text-sm text-gray-medium hover:text-orange-primary"
          >
            {item.name}
          </Link>
        ))}
      </div>
    )}
  </div>
);

const MobileLink = ({ link, isActive, onClick }) => (
  <Link
    to={link.path}
    onClick={onClick}
    className={`block py-3 px-4 text-sm font-medium ${
      isActive ? "text-orange-primary bg-orange-light rounded-lg" : "text-gray-dark"
    }`}
  >
    {link.name}
  </Link>
);

const MobileNav = ({ links, openDropdowns, onToggleDropdown, isActiveFn, onClose, onLaunchDsp }) => (
  <div className="lg:hidden pb-6" data-testid="mobile-menu">
    {links.map((link) =>
      link.dropdown ? (
        <MobileDropdown
          key={link.path}
          link={link}
          isActive={isActiveFn(link.path)}
          isOpen={!!openDropdowns[link.path]}
          onToggle={() => onToggleDropdown(link.path)}
          onItemClick={onClose}
        />
      ) : (
        <MobileLink key={link.path} link={link} isActive={isActiveFn(link.path)} onClick={onClose} />
      )
    )}
    <button onClick={onLaunchDsp} className="btn-primary w-full mt-4 text-sm">
      Launch DSP
    </button>
  </div>
);

// ---------- Main ----------
export const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [openDropdowns, setOpenDropdowns] = useState({});
  const location = useLocation();
  const navigate = useNavigate();

  const toggleDropdown = (path) =>
    setOpenDropdowns((prev) => ({ ...prev, [path]: !prev[path] }));
  const isActive = (path) =>
    location.pathname === path || location.pathname.startsWith(path + "/");

  const launchDsp = () => {
    navigate("/dsp/get-access");
    setIsOpen(false);
  };

  return (
    <nav
      className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md shadow-sm"
      data-testid="main-navigation"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          <Link to="/" className="flex items-center" data-testid="logo-link">
            <img src={LOGO_URL} alt="FunAsia Digital" className="h-14 w-auto" />
          </Link>

          <DesktopNav links={NAV_LINKS} isActiveFn={isActive} onLaunchDsp={launchDsp} />

          <button
            className="lg:hidden p-2"
            onClick={() => setIsOpen(!isOpen)}
            data-testid="mobile-menu-btn"
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {isOpen && (
          <MobileNav
            links={NAV_LINKS}
            openDropdowns={openDropdowns}
            onToggleDropdown={toggleDropdown}
            isActiveFn={isActive}
            onClose={() => setIsOpen(false)}
            onLaunchDsp={launchDsp}
          />
        )}
      </div>
    </nav>
  );
};

export default Navigation;
